import { AxiosInstance, isAxiosError } from 'axios'
import type { RawPodManifest, StorefrontOrder } from '@fa/pod-schema'
import { CampaignAssignment, StorybookTemplate, StorybookTemplateSummary } from './types'
import { storefrontOrderSchema } from './storefrontOrder'
import { storeLandingPageSchema, type StoreLandingPage } from './storeLandingPage'
import {
  GetHashesForBaseImages,
  GetImagesForPreview,
  GetImagesForPreviewResponse,
  GetPhotoProductAndVariants,
  PhotoProduct,
} from './types/photoProducts'

export class PhotoProductsApi {
  api: AxiosInstance

  constructor(api: AxiosInstance) {
    this.api = api
  }

  getPhotoProductAndVariants: GetPhotoProductAndVariants = async (photoProductId: number) => {
    const { data } = await this.api.get<PhotoProduct>(`/v1/photo-products/${photoProductId}`)

    return data
  }

  getHashesForBaseImages: GetHashesForBaseImages = async (variantId: number) => {
    const { data } = await this.api.get<string[]>(`/v1/printify/baseImageHash/${variantId}`)
    return data
  }
  getImagesForPreview: GetImagesForPreview = async (payload) => {
    const {
      data: { productId, images },
    } = await this.api.post<GetImagesForPreviewResponse>(
      `/v1/photo-products/generate-preview`,
      payload
    )

    return {
      images,
      productId,
    }
  }

  getStorybookTemplates = async (): Promise<StorybookTemplateSummary[]> => {
    const { data } = await this.api.get<StorybookTemplateSummary[]>('/v1/storybook-templates')
    return data
  }

  getStorybookTemplate = async (id: number): Promise<StorybookTemplate> => {
    const { data } = await this.api.get<StorybookTemplate>(`/v1/storybook-templates/${id}`)
    return data
  }

  getPodManifest = async (): Promise<RawPodManifest> => {
    const { data } = await this.api.get<RawPodManifest>('/v1/growth/pod/manifest')
    return data
  }

  /**
   * The Campaign Variant this shopper is assigned to.
   *
   * Keyed by Campaign slug alone -- the request names no Variant, because the
   * server owns cohort selection (Hapi ADR 0005). Do not add a Variant
   * parameter here; a client-selectable cohort is the thing that ADR forbids.
   *
   * Authenticated by the growth session cookie, which `withCredentials` sends.
   * 401 means the session is missing or its identity cannot be bucketed; 404
   * means no Campaign is Live on this slug.
   */
  getCampaignAssignment = async (slug: string): Promise<CampaignAssignment> => {
    const { data } = await this.api.get<CampaignAssignment>(
      `/v1/growth/pod/campaigns/${encodeURIComponent(slug)}/assignment`,
    )
    return data
  }

  // Active Store Configuration order for a store-card shopper (Hapi ADR daniel-0029,
  // Frontend ADR daniel-0015). Unauthenticated. 404 (no Active config) → null so
  // the caller falls back to today's sequences. Other errors propagate; the
  // provider collapses them to the same fallback.
  getStoreConfiguration = async (): Promise<StorefrontOrder | null> => {
    try {
      const { data } = await this.api.get<unknown>('/v1/growth/pod/store-configuration')
      return storefrontOrderSchema.parse(data)
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        return null
      }
      throw err
    }
  }

  // The Active Store configuration's pinned landing page (Hapi ADR daniel-0033).
  // 404 (nothing Active, or no pin) → null. Other errors propagate.
  getStoreLandingPage = async (): Promise<StoreLandingPage | null> =>
    this.getPinnedLandingPage('/v1/growth/pod/store-configuration/landing-page')

  /**
   * The public Landing Page Version served at `/photo-products/landing/:slug`
   * (sc-7841; Hapi amends its ADR 0009 on feature/ddp). Unauthenticated, and the
   * same envelope as `getStoreLandingPage`. 404 (no public page for the slug, or
   * a Hapi that predates the seam -- every call until it deploys) → null. Other
   * errors propagate.
   */
  getLandingPageBySlug = async (slug: string): Promise<StoreLandingPage | null> =>
    this.getPinnedLandingPage(`/v1/growth/pod/landing-pages/${encodeURIComponent(slug)}`)

  /**
   * The Landing Page Version an Event pins, by type-slug (`on-birthday`; Hapi ADR yoav-0023):
   * the same envelope as `getStoreLandingPage`. 404 (unknown slug, no pin, or a Hapi before
   * the route) → null.
   */
  getEventLandingPage = async (typeSlug: string): Promise<StoreLandingPage | null> =>
    this.getPinnedLandingPage(
      `/v1/growth/pod/events/${encodeURIComponent(typeSlug)}/landing-page`
    )

  private async getPinnedLandingPage(path: string): Promise<StoreLandingPage | null> {
    try {
      const { data } = await this.api.get<unknown>(path)
      return storeLandingPageSchema.parse(data) as StoreLandingPage
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        return null
      }
      throw err
    }
  }
}
